import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatEUR } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Calendar, Plus, X, Clock, User, Phone, Scissors, ChevronLeft, ChevronRight, Receipt, CheckCircle2, Trash2 } from "lucide-react";
import ScontrinoPreview from "@/components/ScontrinoPreview";
import { newOfflineId } from "@/lib/offlineQueue";
import { recoverUnreachableSale, SERVER_ATTEMPT } from "@/lib/offlineSales";

const STATUS_COLORS = {
  scheduled: { bg: "#3B82F6", label: "Prenotato" },
  in_progress: { bg: "#F59E0B", label: "In corso" },
  completed: { bg: "#2F9E77", label: "Completato" },
  cancelled: { bg: "#94A3B8", label: "Annullato" },
  no_show: { bg: "#DC2626", label: "No-show" },
};

// Slot orari 8:00 - 20:30 ogni 30 min
const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 8; h < 21; h++) {
    for (let m = 0; m < 60; m += 30) {
      slots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return slots;
})();

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, n) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const formatDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" });
};

export default function POSParrucchiere() {
  const [tenant, setTenant] = useState(null);
  const [date, setDate] = useState(todayISO());
  const [appts, setAppts] = useState([]);
  const [services, setServices] = useState([]); // prodotti = servizi
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ customer_name: "", customer_phone: "", service_name: "", service_price: 0, operator: "", start_at: "", duration_min: 30, notes: "" });
  const [showScontrino, setShowScontrino] = useState(false);
  const [last, setLast] = useState(null);

  useEffect(() => {
    api.get("/tenant/me").then(r => setTenant(r.data));
    api.get("/products?mode=parrucchiere").then(r => setServices(Array.isArray(r.data) ? r.data : []));
  }, []);

  useEffect(() => {
    api.get(`/appointments?date=${date}&kind=beauty`).then(r => setAppts(Array.isArray(r.data) ? r.data : []));
  }, [date]);

  const reload = () => api.get(`/appointments?date=${date}&kind=beauty`).then(r => setAppts(Array.isArray(r.data) ? r.data : []));

  const openNew = (slot) => {
    setEditing(null);
    setForm({
      customer_name: "",
      customer_phone: "",
      service_name: services[0]?.name || "",
      service_price: services[0]?.price || 0,
      operator: "",
      start_at: `${date}T${slot || "09:00"}:00`,
      duration_min: services[0]?.duration_min || 30,
      notes: "",
    });
    setShowNew(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      customer_name: a.customer_name || "",
      customer_phone: a.customer_phone || "",
      service_name: a.service_name || "",
      service_price: a.service_price || 0,
      operator: a.operator || "",
      start_at: a.start_at,
      duration_min: a.duration_min || 30,
      notes: a.notes || "",
    });
    setShowNew(true);
  };

  const save = async () => {
    if (!form.customer_name.trim()) return toast.error("Nome cliente richiesto");
    if (!form.service_name.trim()) return toast.error("Servizio richiesto");
    try {
      if (editing) {
        await api.patch(`/appointments/${editing.id}`, form);
        toast.success("Appuntamento aggiornato");
      } else {
        await api.post("/appointments", { ...form, kind: "beauty" });
        toast.success("Appuntamento creato");
      }
      setShowNew(false);
      reload();
    } catch { toast.error("Errore"); }
  };

  const changeStatus = async (a, status) => {
    try {
      await api.patch(`/appointments/${a.id}/status?status=${status}`);
      toast.success(`→ ${STATUS_COLORS[status].label}`);
      reload();
    } catch { toast.error("Errore"); }
  };

  const delAppt = async (a) => {
    if (!window.confirm("Eliminare appuntamento?")) return;
    try {
      await api.delete(`/appointments/${a.id}`);
      toast.success("Eliminato");
      reload();
    } catch { toast.error("Errore"); }
  };

  const checkout = async (a) => {
    const offlineId = newOfflineId();
    const lines = [{ name: a.service_name, qty: 1, price: a.service_price, vat: 22 }];
    let oid = null;
    try {
      const r = await api.post("/orders", {
        mode: "parrucchiere",
        lines,
        appointment_id: a.id,
        offline_id: offlineId,
      }, SERVER_ATTEMPT);
      oid = r.data.id;
      const c = await api.post(`/orders/${oid}/close-fiscal`, { payment_method: "cash" }, SERVER_ATTEMPT);
      setLast(c.data); setShowScontrino(true);
      reload();
      toast.success(`Scontrino #${c.data.receipt_number}`);
      api.patch(`/appointments/${a.id}/status?status=completed`).catch(() => {
        toast.error("Scontrino emesso. Lo stato dell'appuntamento si aggiorna quando torna la linea.");
      });
      return;
    } catch (e) {
      const off = await recoverUnreachableSale(e, {
        tenant, offline_id: offlineId, server_order_id: oid, mode: "parrucchiere", lines,
        payment_method: "cash", notes: a.customer_name || null,
      });
      if (off.printed) {
        setLast(off.order);
        setShowScontrino(true);
        api.patch(`/appointments/${a.id}/status?status=completed`).catch(() => {});
        return;
      }
      if (!off.handled) toast.error("Errore");
    }
  };

  const apptsBySlot = useMemo(() => {
    const map = {};
    appts.forEach(a => {
      const hhmm = a.start_at.slice(11, 16);
      if (!map[hhmm]) map[hhmm] = [];
      map[hhmm].push(a);
    });
    return map;
  }, [appts]);

  const stats = useMemo(() => {
    const c = appts.length;
    const revenue = (Array.isArray(appts) ? appts : []).filter(a => a.status === "completed").reduce((s, a) => s + (a.service_price || 0), 0);
    return { count: c, revenue };
  }, [appts]);

  return (
    <div className="h-screen bg-velta-bg flex flex-col overflow-hidden">
      <header className="h-14 border-b border-velta-border bg-white flex items-center px-4 gap-4 shrink-0">
        <Link to="/cassa" data-testid="parr-back" className="inline-flex items-center gap-2 text-velta-mute hover:text-velta-ink text-sm font-medium">
          <ArrowLeft className="w-4 h-4" /> Modalità
        </Link>
        <div className="h-6 w-px bg-velta-border" />
        <div className="font-heading font-semibold tracking-tight">Velta One · Parrucchiere</div>
        <div className="ml-auto flex items-center gap-3">
          <div className="text-xs text-velta-mute"><span data-testid="parr-stats-count">{stats.count}</span> app. · <span data-testid="parr-stats-revenue">{formatEUR(stats.revenue)}</span></div>
          <button
            data-testid="parr-new-btn"
            onClick={() => openNew(null)}
            className="h-10 px-4 bg-[#D946EF] text-white rounded-md font-medium btn-tactile inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Nuovo
          </button>
        </div>
      </header>

      {/* Date navigator */}
      <div className="h-14 border-b border-velta-border bg-white flex items-center justify-center gap-4">
        <button data-testid="parr-prev-day" onClick={() => setDate(addDays(date, -1))} className="w-9 h-9 border border-velta-border rounded-md flex items-center justify-center hover:border-velta-ink"><ChevronLeft className="w-4 h-4" /></button>
        <button data-testid="parr-today" onClick={() => setDate(todayISO())} className="px-4 h-9 border border-velta-border rounded-md text-sm hover:border-velta-ink">Oggi</button>
        <div className="font-heading font-semibold text-lg capitalize" data-testid="parr-current-date">{formatDate(date)}</div>
        <input
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          data-testid="parr-date-picker"
          className="h-9 px-2 border border-velta-border rounded-md text-sm"
        />
        <button data-testid="parr-next-day" onClick={() => setDate(addDays(date, 1))} className="w-9 h-9 border border-velta-border rounded-md flex items-center justify-center hover:border-velta-ink"><ChevronRight className="w-4 h-4" /></button>
      </div>

      <div className="flex-1 overflow-auto pos-scroll p-4">
        <div className="max-w-5xl mx-auto space-y-1">
          {(Array.isArray(TIME_SLOTS) ? TIME_SLOTS : []).map(slot => {
            const slotAppts = apptsBySlot[slot] || [];
            return (
              <div key={slot} className="flex items-stretch gap-3">
                <button
                  data-testid={`parr-slot-${slot}`}
                  onClick={() => openNew(slot)}
                  className="w-20 shrink-0 text-xs font-mono text-velta-mute text-right pt-2 hover:text-velta-ink"
                >
                  {slot}
                </button>
                <div className="flex-1 min-h-[40px] border-l-2 border-velta-border pl-3 py-1">
                  {slotAppts.length === 0 ? (
                    <button
                      onClick={() => openNew(slot)}
                      className="w-full h-full min-h-[36px] text-xs text-velta-mute hover:bg-velta-bg rounded-md transition"
                    >
                      + libero
                    </button>
                  ) : (
                    <div className="space-y-1">
                      {(Array.isArray(slotAppts) ? slotAppts : []).map(a => {
                        const sc = STATUS_COLORS[a.status] || STATUS_COLORS.scheduled;
                        return (
                          <div
                            key={a.id}
                            data-testid={`parr-appt-${a.id}`}
                            className="bg-white border-l-4 rounded-md p-3 hover:shadow-sm"
                            style={{ borderColor: sc.bg }}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 cursor-pointer" onClick={() => openEdit(a)}>
                                <div className="flex items-center gap-2 mb-1">
                                  <User className="w-3.5 h-3.5 text-velta-mute" />
                                  <span className="font-semibold">{a.customer_name}</span>
                                  {a.customer_phone && <span className="text-xs text-velta-mute"><Phone className="w-3 h-3 inline" /> {a.customer_phone}</span>}
                                </div>
                                <div className="flex items-center gap-3 text-sm">
                                  <span className="text-velta-ink">{a.service_name}</span>
                                  <span className="text-xs text-velta-mute"><Clock className="w-3 h-3 inline" /> {a.duration_min} min</span>
                                  {a.operator && <span className="text-xs text-velta-mute"><Scissors className="w-3 h-3 inline" /> {a.operator}</span>}
                                  <span className="font-mono font-medium text-velta-ink ml-auto">{formatEUR(a.service_price || 0)}</span>
                                </div>
                              </div>
                              <div className="flex flex-col gap-1 items-end">
                                <span className="text-[10px] font-medium px-2 py-0.5 rounded text-white" style={{ background: sc.bg }}>{sc.label}</span>
                                <div className="flex gap-1">
                                  {a.status === "scheduled" && (
                                    <button data-testid={`parr-start-${a.id}`} onClick={() => changeStatus(a, "in_progress")} className="w-7 h-7 border border-velta-border rounded text-xs hover:bg-velta-bg" title="Inizia"><Clock className="w-3 h-3 mx-auto" /></button>
                                  )}
                                  {a.status !== "completed" && a.status !== "cancelled" && (
                                    <button data-testid={`parr-checkout-${a.id}`} onClick={() => checkout(a)} className="w-7 h-7 bg-[#2F9E77] text-white rounded text-xs btn-tactile" title="Chiudi scontrino"><Receipt className="w-3 h-3 mx-auto" /></button>
                                  )}
                                  {a.status !== "completed" && (
                                    <button data-testid={`parr-cancel-${a.id}`} onClick={() => changeStatus(a, "cancelled")} className="w-7 h-7 border border-velta-border rounded text-xs hover:bg-red-50 text-velta-error" title="Annulla"><X className="w-3 h-3 mx-auto" /></button>
                                  )}
                                  <button data-testid={`parr-del-${a.id}`} onClick={() => delAppt(a)} className="w-7 h-7 border border-velta-border rounded text-xs hover:bg-red-50 text-velta-error" title="Elimina"><Trash2 className="w-3 h-3 mx-auto" /></button>
                                </div>
                              </div>
                            </div>
                            {a.notes && <div className="text-xs text-velta-mute mt-2 italic">&ldquo;{a.notes}&rdquo;</div>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* New / Edit modal */}
      {showNew && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setShowNew(false)}>
          <div className="bg-white rounded-md w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-2xl font-semibold">{editing ? "Modifica" : "Nuovo"} appuntamento</h3>
              <button onClick={() => setShowNew(false)} data-testid="parr-modal-close"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-velta-mute">Nome cliente *</label>
                  <input data-testid="parr-form-name" value={form.customer_name} onChange={e => setForm({ ...form, customer_name: e.target.value })} className="w-full h-10 px-3 border border-velta-border rounded-md" />
                </div>
                <div>
                  <label className="text-xs text-velta-mute">Telefono</label>
                  <input data-testid="parr-form-phone" value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })} className="w-full h-10 px-3 border border-velta-border rounded-md" />
                </div>
              </div>

              <div>
                <label className="text-xs text-velta-mute">Servizio *</label>
                <select
                  data-testid="parr-form-service"
                  value={form.service_name}
                  onChange={e => {
                    const svc = services.find(s => s.name === e.target.value);
                    setForm({
                      ...form,
                      service_name: e.target.value,
                      service_price: svc?.price || form.service_price,
                      duration_min: svc?.duration_min || form.duration_min,
                    });
                  }}
                  className="w-full h-10 px-3 border border-velta-border rounded-md"
                >
                  {services.length === 0 && <option value="">Aggiungi servizi in Articoli</option>}
                  {(Array.isArray(services) ? services : []).map(s => <option key={s.id} value={s.name}>{`${s.name} — €${(s.price || 0).toFixed(2)}`}</option>)}
                  <option value="__custom">Personalizzato…</option>
                </select>
                {form.service_name === "__custom" && (
                  <input data-testid="parr-form-service-custom" placeholder="Nome servizio" value={form.service_name === "__custom" ? "" : form.service_name} onChange={e => setForm({ ...form, service_name: e.target.value })} className="mt-2 w-full h-10 px-3 border border-velta-border rounded-md" />
                )}
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs text-velta-mute">Prezzo €</label>
                  <input data-testid="parr-form-price" type="number" step="0.01" value={form.service_price} onChange={e => setForm({ ...form, service_price: parseFloat(e.target.value) || 0 })} className="w-full h-10 px-3 border border-velta-border rounded-md" />
                </div>
                <div>
                  <label className="text-xs text-velta-mute">Durata (min)</label>
                  <input data-testid="parr-form-duration" type="number" step="5" value={form.duration_min} onChange={e => setForm({ ...form, duration_min: parseInt(e.target.value) || 30 })} className="w-full h-10 px-3 border border-velta-border rounded-md" />
                </div>
                <div>
                  <label className="text-xs text-velta-mute">Operatore</label>
                  <input data-testid="parr-form-operator" value={form.operator} onChange={e => setForm({ ...form, operator: e.target.value })} className="w-full h-10 px-3 border border-velta-border rounded-md" />
                </div>
              </div>

              <div>
                <label className="text-xs text-velta-mute">Data e ora *</label>
                <input
                  data-testid="parr-form-datetime"
                  type="datetime-local"
                  value={form.start_at.slice(0, 16)}
                  onChange={e => setForm({ ...form, start_at: e.target.value + ":00" })}
                  className="w-full h-10 px-3 border border-velta-border rounded-md"
                />
              </div>

              <div>
                <label className="text-xs text-velta-mute">Note</label>
                <textarea data-testid="parr-form-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows="2" className="w-full px-3 py-2 border border-velta-border rounded-md" />
              </div>

              <div className="flex gap-2 pt-2">
                <button data-testid="parr-form-cancel" onClick={() => setShowNew(false)} className="flex-1 h-12 border border-velta-border rounded-md font-medium">Annulla</button>
                <button data-testid="parr-form-save" onClick={save} className="flex-1 h-12 bg-[#D946EF] text-white rounded-md font-medium btn-tactile">Salva</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showScontrino && <ScontrinoPreview order={last} tenant={tenant} fiscal onClose={() => setShowScontrino(false)} />}
    </div>
  );
}
